import type { ActionErrorJson } from '@/backend/actions/test-helpers/createActionErrorFromJson';
import { ActionError } from '@/backend/errors/ActionError';
import { findRelevantProblem } from '@/backend/errors/findRelevantProblem';
import type {
  InvalidResult,
  ValidationError,
} from '@/backend/errors/ValidationError';

/**
 * Intended to eventually replace ActionError this error class has several helpful features:
 *
 * - We deliberately separate out three kinds of messages:
 *   - A canonical error message for testing which kind of error this is and grouping errors in sentry
 *   - A detailed error message that provides details in a glance for debugging the error
 *   - A customer facing error message that provides a less technical message suitable for display in the UI
 *
 * - We support recording arbitrary additional details in the relatedDetails field
 *
 * - The httpStatusCode field is first and doesn't default to anything which will encourage engineers to
 *   fill it out and not have a bunch of errors with a 500 code which are actually 400 errors.
 *
 * - The fingerprint ignores the detailedErrorMessage so that similar errors are grouped in sentry
 *
 * - The default customer facing error message includes the detailedErrorMessage so that if an error
 *   isn't intended to be customer facing we still have a way to distinguish between it and other unknown errors
 *
 * - The toJson method includes all of the additional detail we're capturing
 */
export class FriendlyError extends ActionError {
  constructor(
    /**
     * 4XX error if this is an error caused by a bad request
     * 5XX error if this error is unexpected or caused by a failed external request
     */
    httpStatusCode: number,
    /**
     * A short string which identifies this kind of error,
     * should not include any details which might change from instance to instance
     * should never be updated unless splitting error types into more granular error types
     *
     * @example user-not-found
     * @example duplicate-recipient-email
     */
    public readonly canonicalErrorMessage: string,
    /**
     * A longer string which gives a detailed description of the error,
     * should include as much detail as possible, except that
     * should be limited to 1-2 80 character lines
     *
     * Avoid putting PII such as names or email addresses in these messages.
     * Never put sensitive PII such as SSNs in these messages.
     */
    public readonly detailedErrorMessage: string,
    /**
     * A longer string which gives a useful description of the error to the end user
     * should either
     * - include only customer relevant details (if this error is likely to occur only due to user error, and especially if the user can correct the error)
     * - include an appeal to contact support and a copy of the detailed error message (if this error is likely due to programmer error, and especially if manual resolution or debugging will be required)
     *
     * Should be limited to 1-2 80 character lines
     */
    public readonly customerFacingMessage: string = `An error occurred - please contact support, the technical details are: ${detailedErrorMessage}`,
    /**
     * Details useful for debugging this error, should be:
     * - always a string
     * - short
     * - easy to identify (e.g. userId, not id)
     */
    public readonly relatedDetails: Record<string, string | undefined> = {},
    /**
     * The error or errors which triggered this error
     */
    inner: null | Error | Error[] | ValidationError | ValidationError[] = null,
  ) {
    super(
      detailedErrorMessage,
      inner ?? null,
      canonicalErrorMessage,
      httpStatusCode,
    );
  }

  isFriendlyError = true;

  get severity(): 'requires-intervention' | 'user-error' {
    return this.httpStatusCode >= 500 ? 'requires-intervention' : 'user-error';
  }

  details() {
    return {
      ...super.details(),
      ...this.relatedDetails,
    };
  }

  selfFingerprint(): string[] {
    return [...this.path, this.canonicalErrorMessage];
  }

  toJSON(): ActionErrorJson {
    return {
      ...super.toJSON(),
      severity: this.severity,
      canonicalErrorMessage: this.canonicalErrorMessage,
      detailedErrorMessage: this.detailedErrorMessage,
      customerFacingMessage: this.customerFacingMessage,
      relatedDetails: this.relatedDetails,
    };
  }

  static Kind<Details extends Record<string, string>>(
    statusCode: number,
    canonicalErrorMessage: string,
    getDetailedErrorMessage: (
      details: Details,
      inner: null | Error | Error[],
    ) => string,
    getCustomerFacingMessage: (
      details: Details,
      inner: null | Error | Error[],
      detailedErrorMessage: string,
    ) => string,
  ): { new (details: Details, inner?: null | Error | Error[]): FriendlyError } {
    // This funny way of returning the class will give the class the 'name' of the canonical error message,
    // although this doesn't happen in every case.
    return {
      [canonicalErrorMessage]: class extends FriendlyError {
        constructor(details: Details, inner: null | Error | Error[] = null) {
          const detailedErrorMessage = getDetailedErrorMessage(details, inner);
          super(
            statusCode,
            canonicalErrorMessage,
            detailedErrorMessage,
            getCustomerFacingMessage(details, inner, detailedErrorMessage),
            details,
            inner,
          );
        }

        static kind = canonicalErrorMessage;
      },
    }[canonicalErrorMessage];
  }

  static fromValidationError(
    error: InvalidResult,
    message: string = `Invalid data was encountered during the operation`,
    relatedDetails: Record<string, string> = {},
  ): FriendlyError {
    return new FriendlyError(
      400,
      error.problemCode,
      findRelevantProblem(error),
      message,
      relatedDetails,
      error.toException(),
    );
  }

  /**
   *
   * @param message Why the code is unreachable
   */
  static fromUnreachable(message: string) {
    return new FriendlyError(500, `unreachable`, `Unreachable: ${message}`);
  }

  static fromNever(
    action: never,
    message = `Case should be unreachable: ${action as string}`,
  ) {
    throw new FriendlyError(500, 'unreachable-case', message);
  }

  static assertDefined<T>(
    condition: T | null | undefined,
    message: string,
  ): asserts condition is T {
    if (condition == null) {
      throw this.fromUnreachable(message);
    }
  }

  static assertTrue(
    condition: boolean,
    message: string,
  ): asserts condition is true {
    if (condition == null) {
      throw this.fromUnreachable(message);
    }
  }

  static notImplemented(message?: string) {
    return new FriendlyError(
      500,
      'not-implemented',
      'Not implemented' + message ? ': ' + message : '',
    );
  }

  /**
   * Creates a short list of problems ending in `...5 more` if there are more than 5.
   */
  static listProblems(problems: string[]) {
    return `${problems
      .slice(0, 5)
      .join(
        ', ',
      )}${problems.length > 5 ? `...${problems.length - 5} more` : ''}`;
  }
}

export function quacksLikeFriendlyError(error: any): error is {
  httpStatusCode: number;
  canonicalErrorMessage: string;
  detailedErrorMessage: string;
  customerFacingMessage: string;
  relatedDetails?: Record<string, string>;
} {
  return !!(
    error.httpStatusCode &&
    error.canonicalErrorMessage &&
    error.detailedErrorMessage &&
    error.customerFacingMessage
  );
}

export function quacksLikeRequestError(
  error: any,
): error is { response: { data: { error: unknown } } } {
  return !!error?.response?.data?.error;
}

export function quacksLikeActionError(error: any): error is ActionError {
  return !!(error.problemCode && error.description && error.httpStatusCode);
}
